/**
 * SyncEngineFallbackBanner
 *
 * Shown when the native (main-process) offline sync engine reports that it
 * cannot run — previously the user got no signal at all beyond a blank Sales
 * page and, eventually, a misleading forced sign-out. Now we:
 *   1. surface a clear, honest banner ("offline sync engine failed — retrying"),
 *   2. automatically switch to the in-renderer @powersync/web fallback so data
 *      still loads, and
 *   3. hide the banner once sync recovers (the fallback connects and syncs).
 *
 * The `sync-engine-failed` event is relayed from main via electronDb.ts onto
 * the shared `syncEvents` bus. Electron-only; on the web build the fallback
 * client is already the primary engine so this never triggers.
 *
 * This component also surfaces `local-db-unrecoverable` / `local-db-recovered`
 * (dispatched by ipcBridge.ts's circuit breaker and database.ts's self-heal,
 * see recoverLocalDatabase()) — a corrupted local IndexedDB database on
 * whichever engine currently backs @powersync/web, applicable on both web and
 * Electron's web-fallback mode.
 */

import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { useSyncStatus } from '@hooks/useSyncStatus';
import {
  db,
  enableWebFallback,
  isUsingWebFallback,
} from '@/renderer/lib/powersync/database';
import { syncEvents } from '@/renderer/lib/powersync/supabaseConnector';
import { isElectron } from '@/renderer/util';

export function SyncEngineFallbackBanner() {
  const [failed, setFailed] = useState(false);
  // Describes local writes left behind on the abandoned native database when
  // the fallback engine took over — not cleared just because the new web
  // engine is syncing fine, since that says nothing about the abandoned
  // queue. Cleared below once a recovery back to the native engine confirms
  // that queue is actually empty (drained by the native engine's own
  // background retries, or the user redid the affected action) — see the
  // polling effect below. Falls back to a page reload if recovery never
  // happens.
  const [dataAtRisk, setDataAtRisk] = useState<number | null>(null);
  // The repeated-sync-error breaker (powersyncHandlers.ts) disconnects a
  // device after 3 identical PSYNC_S* errors to avoid hammering a possibly
  // broken sync rule — but previously that left the device silently
  // stranded offline with no signal and no way back short of an app
  // restart. This surfaces it and offers a manual reconnect instead.
  const [streamError, setStreamError] = useState<string | null>(null);
  const [reconnecting, setReconnecting] = useState(false);
  // The local-db circuit breaker (ipcBridge.ts) trips after a burst of
  // corrupted-local-database failures (e.g. sqlite3_open_v2) and signals
  // database.ts to delete and recreate the local IndexedDB file. That
  // recovery is automatic and normally takes a second or two — this just
  // keeps the page from looking silently broken/frozen while it happens.
  const [recoveringLocalDb, setRecoveringLocalDb] = useState(false);
  const { connected, hasSynced } = useSyncStatus();

  useEffect(() => {
    const handler = () => {
      setFailed(true);
      // Switch `db` to the in-renderer @powersync/web client. Idempotent.
      enableWebFallback();
    };
    const dataAtRiskHandler = (event: Event) => {
      const { pendingCount } = (event as CustomEvent<{ pendingCount: number }>)
        .detail;
      setDataAtRisk(pendingCount);
    };
    const streamErrorHandler = (event: Event) => {
      const { message } = (event as CustomEvent<{ message: string }>).detail;
      setStreamError(message);
    };
    const localDbUnrecoverableHandler = () => setRecoveringLocalDb(true);
    const localDbRecoveredHandler = () => setRecoveringLocalDb(false);
    syncEvents.addEventListener('sync-engine-failed', handler);
    syncEvents.addEventListener(
      'sync-engine-fallback-data-at-risk',
      dataAtRiskHandler,
    );
    syncEvents.addEventListener(
      'sync-stream-error-repeated',
      streamErrorHandler,
    );
    syncEvents.addEventListener(
      'local-db-unrecoverable',
      localDbUnrecoverableHandler,
    );
    syncEvents.addEventListener('local-db-recovered', localDbRecoveredHandler);

    // Proactive pull: the main process may have emitted 'sync-engine-failed'
    // during its own startup (activating the last outlet) before this window
    // existed, so that push was lost. Ask directly whether the native engine is
    // usable. Skip when we're already on the web fallback from a previous run
    // (nativeKnownBroken) — that is the steady state, not an error to announce.
    if (isElectron() && !isUsingWebFallback()) {
      window.powersync
        ?.isNativeEngineAvailable?.()
        .then((available) => {
          if (!available) handler();
          return available;
        })
        .catch(() => {
          /* IPC unavailable — nothing we can do; leave native path in place. */
        });
    }

    return () => {
      syncEvents.removeEventListener('sync-engine-failed', handler);
      syncEvents.removeEventListener(
        'sync-engine-fallback-data-at-risk',
        dataAtRiskHandler,
      );
      syncEvents.removeEventListener(
        'sync-stream-error-repeated',
        streamErrorHandler,
      );
      syncEvents.removeEventListener(
        'local-db-unrecoverable',
        localDbUnrecoverableHandler,
      );
      syncEvents.removeEventListener(
        'local-db-recovered',
        localDbRecoveredHandler,
      );
    };
  }, []);

  const handleReconnect = async () => {
    setReconnecting(true);
    try {
      await window.powersync?.reconnect?.();
      setStreamError(null);
    } catch (err) {
      console.error('[PowerSync] Manual reconnect failed:', err);
    } finally {
      setReconnecting(false);
    }
  };

  // Once the fallback engine has actually connected (or completed a first sync),
  // the data is flowing again — drop the transient "failed" banner. The
  // dataAtRisk banner is deliberately untouched by this — synced state says
  // nothing about whether the abandoned native database's queue was lost.
  useEffect(() => {
    if (failed && (connected || hasSynced)) setFailed(false);
  }, [failed, connected, hasSynced]);

  // Safety net in case 'local-db-recovered' is missed (e.g. component
  // remounted mid-recovery) — a synced connection proves the local database
  // is usable again regardless of which event fired.
  useEffect(() => {
    if (recoveringLocalDb && (connected || hasSynced)) {
      setRecoveringLocalDb(false);
    }
  }, [recoveringLocalDb, connected, hasSynced]);

  // Clear automatically if the device reconnects on its own (e.g. a manual
  // reconnect elsewhere, or the underlying sync-rule issue got fixed and a
  // later status tick came back clean) — not just via the button below.
  useEffect(() => {
    if (streamError && connected) setStreamError(null);
  }, [streamError, connected]);

  // The abandoned native queue can only actually drain once we're back on
  // the native engine (a manual "Resync Now" elsewhere in the app calls
  // tryRecoverNativeEngine()) — check periodically whether that's happened
  // and, if so, whether the queue it left behind has since emptied (either
  // because the native engine's own background retries eventually uploaded
  // it, or the user redid the affected orders). Read-only; never assumes
  // data is safe without actually checking the current queue depth.
  useEffect(() => {
    if (dataAtRisk === null) return undefined;
    let cancelled = false;

    const checkRecovered = async () => {
      if (isUsingWebFallback()) return;
      try {
        const stats = await db.getUploadQueueStats();
        if (!cancelled && stats.count === 0) setDataAtRisk(null);
      } catch {
        // Native engine not reachable yet — leave the banner up and retry.
      }
    };

    checkRecovered();
    const intervalId = window.setInterval(checkRecovered, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [dataAtRisk]);

  if (dataAtRisk !== null) {
    const description =
      dataAtRisk < 0
        ? 'may have had unsynced orders/items'
        : `had ${dataAtRisk} unsynced order/item change${dataAtRisk === 1 ? '' : 's'}`;
    return (
      <div className="fixed top-0 left-0 right-0 z-[9999] flex items-center justify-center gap-2 border-b-2 border-red-500 bg-red-50 px-4 py-2 shadow-sm">
        <AlertTriangle size={15} className="text-red-600" />
        <span className="text-sm font-medium text-red-800">
          Offline sync engine switched modes — the previous local database{' '}
          {description} that will not be uploaded automatically. Please check
          recent orders and contact support if anything looks missing.
        </span>
      </div>
    );
  }

  if (recoveringLocalDb) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[9999] flex items-center justify-center gap-2 border-b-2 border-pos-primary bg-green-50 px-4 py-2 shadow-sm">
        <Loader2 size={14} className="animate-spin text-pos-primary" />
        <span className="text-sm font-medium text-green-800">
          Recovering local data — this only takes a moment…
        </span>
      </div>
    );
  }

  if (failed) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[9999] flex items-center justify-center gap-2 border-b-2 border-amber-400 bg-amber-50 px-4 py-2 shadow-sm">
        <AlertTriangle size={15} className="text-amber-600" />
        <span className="text-sm font-medium text-amber-800">
          Offline sync engine failed to start — switching to compatibility mode
          and retrying…
        </span>
        <Loader2 size={14} className="animate-spin text-amber-600" />
      </div>
    );
  }

  if (streamError) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[9999] flex items-center justify-center gap-2 border-b-2 border-amber-400 bg-amber-50 px-4 py-2 shadow-sm">
        <AlertTriangle size={15} className="text-amber-600" />
        <span className="text-sm font-medium text-amber-800">
          Sync stopped after repeated errors — local orders are still saved, but
          new changes won&apos;t sync until reconnected.
        </span>
        <button
          type="button"
          onClick={handleReconnect}
          disabled={reconnecting}
          className="flex items-center gap-1 rounded border border-amber-400 bg-white px-2 py-0.5 text-xs font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-60"
        >
          <RefreshCw
            size={12}
            className={reconnecting ? 'animate-spin' : undefined}
          />
          Reconnect
        </button>
      </div>
    );
  }

  return null;
}
